/**
 * Firebase の初期化(唯一の入口)。
 * - APIキーのハードコード禁止。値はすべて .env(VITE_FIREBASE_*)から読む
 * - 本番プロジェクトを作ったら、prod への接続は VITE_APP_ENV=prod のビルドのみに限定する
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserSessionPersistence,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

function readConfig() {
  const missing = REQUIRED_KEYS.filter((k) => !import.meta.env[k]);
  if (missing.length > 0) {
    throw new Error(
      `.env の設定が不足しています: ${missing.join(', ')}\n` +
        '.env.example をコピーして .env を作り、Firebaseコンソールの値を入れてください。',
    );
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

export const appEnv: 'dev' | 'prod' = import.meta.env.VITE_APP_ENV === 'prod' ? 'prod' : 'dev';

/**
 * 使う Firestore データベース(2026-09-12。D-65)。
 * 本番は `(default)`、テストは同じプロジェクト内の `test`。データは完全に別で同期しない。
 * 未設定なら `(default)`(本番)。**テストのつもりで本番を書き換えないよう、prod ビルド以外で
 * `(default)` を指していたら止める**(下の ensureApp)
 */
export const firestoreDbId: string = (import.meta.env.VITE_FIRESTORE_DB as string | undefined)?.trim() || '(default)';

/** 副次アプリ(管理者によるユーザー作成)の初期化に使う。通常の画面からは呼ばない */
export function getFirebaseConfig() {
  return readConfig();
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  const config = readConfig();

  // 事故防止: dev ビルドで本番プロジェクトに繋がっていたら止める
  if (appEnv === 'dev' && config.projectId.includes('prod')) {
    throw new Error(
      `開発ビルドが本番プロジェクト(${config.projectId})に接続しようとしています。.env を確認してください。`,
    );
  }
  // 事故防止2: 本番ビルド以外がエミュレータでも `test` でもなく、本番の (default) を指している
  if (appEnv !== 'prod' && firestoreDbId === '(default)' && import.meta.env.VITE_USE_EMULATOR !== 'true') {
    throw new Error(
      '開発ビルドが本番のデータベース((default))に接続しようとしています。VITE_FIRESTORE_DB=test を設定するか、エミュレータを使ってください。',
    );
  }

  _app = initializeApp(config);

  if (import.meta.env.VITE_USE_EMULATOR === 'true') {
    connectAuthEmulator(getFirebaseAuth(), 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(_app, firestoreDbId), '127.0.0.1', 8080);
  }
  return _app;
}

export function getFirebaseApp(): FirebaseApp {
  return ensureApp();
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    // テスト環境のプレビュー配信(*.pages.dev のブランチURL)や ?tab 付きで開いたタブは
    // ログインをタブ単位に保ち、同じブラウザの別タブに別ロールで入れる(検証用)。
    // それ以外はログインしたまま(IndexedDB)にする — yojitsu-system と同じ方式
    const tabScoped = (() => {
      try {
        const host = window.location.hostname;
        const previewHost = host.endsWith('.pages.dev') && host !== 'cc-system.pages.dev';
        if (previewHost || new URLSearchParams(window.location.search).has('tab')) {
          sessionStorage.setItem('auth-scope', 'tab');
        }
        return sessionStorage.getItem('auth-scope') === 'tab';
      } catch {
        return false;
      }
    })();
    _auth = initializeAuth(ensureApp(), {
      persistence: tabScoped ? browserSessionPersistence : indexedDBLocalPersistence,
    });
  }
  return _auth;
}

export function getDb(): Firestore {
  if (!_db) _db = getFirestore(ensureApp(), firestoreDbId);
  return _db;
}

export function getProjectId(): string {
  return readConfig().projectId;
}
